import { Badge } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsArrowDownLeft, BsArrowUpRight, BsFileTextFill } from "react-icons/bs";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";

const TransactionDetailsView = ({ setViewDetailsShowModal, item }) => {
  const { t } = useTranslation();

  const [transactionData, setTransactionData] = useState(null);

  useEffect(() => {
    async function fetchDetails() {
      try {
        const payload = {
          transaction_id: item._id,
        };

        const result = await apiGet(apiPath.getTransactionDetails, payload);

        const response = result?.data?.results;
        const resultStatus = result?.data?.success;
        if (!resultStatus) {
          setViewDetailsShowModal(false);
          return;
        }

        setTransactionData(response);
      } catch (error) {
        console.log("error in getting transaction details", error);
        setViewDetailsShowModal(false);
        return;
      }
    }

    fetchDetails();
  }, [item._id, setViewDetailsShowModal]);

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
      case "pending":
        return "bg-amber-500/10 text-amber-400 border-amber-500/20";
      case "failed":
        return "bg-red-500/10 text-red-400 border-red-500/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const formatCurrency = (amount, currency_code) => {
    var formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
    return formatted.substring(1, formatted.length) + " " + currency_code;
  };

  if (transactionData == null) {
    return <></>;
  }

  return (
    <>
      <div className="justify-center items-center  overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto ">
          <div className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col bg-white outline-none focus:outline-none w-[1000px] m-auto" style={{ maxHeight: "80vh", overflowY: "auto" }}>
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <div>
                  <h3 className="text-xl font-semibold">{t("O_TRANSACTION_DETAILS")}</h3>
                  <h6>{transactionData.transaction_type}</h6>
                </div>
                <button
                  className=" ml-auto flex items-center justify-center  text-black border-2 rounded-full  h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                  onClick={() => setViewDetailsShowModal(false)}
                >
                  <span className=" text-[#B8BBBF]  text-4xl " title={t("CLOSE")}>
                    ×
                  </span>
                </button>
              </div>

              <div className="space-y-6 m-4">
                {/* Transaction Overview */}
                <div className="rounded-lg bg-muted/30 border border-border p-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-1 col-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_TRANSACTION_ID")}</div>
                      <div className="font-mono text-sm">{transactionData.transaction_id}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_CREATED_AT")}</div>
                      <div className="text-sm">{dayjs(transactionData.createdAt).format("DD/MM/YYYY, HH:mm:ss")}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Status</div>
                      <Badge variant="outline" className={`${getStatusColor(transactionData.status)} rounded-lg py-1 px-2`}>
                        {t(transactionData.status)}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">{t("O_TOTAL_AMOUNT")}</div>
                      <div className="text-lg font-semibold">{formatCurrency(transactionData.total_amount, transactionData.currency_code)}</div>
                    </div>
                  </div>

                  {transactionData.description && (
                    <div className="space-y-1 mt-2 w-100">
                      <div className="text-sm text-muted-foreground">{t("O_DESCRIPTION")}</div>
                      <div className="font-semibold">{transactionData.description}</div>
                    </div>
                  )}

                  {transactionData.metadata && transactionData.metadata.length > 0 && (
                    <div className="space-y-1 mt-2 w-100">
                      <div className="text-sm text-muted-foreground">{t("O_METADATA")}</div>
                      <div className="font-semibold">
                        {transactionData.metadata.map((it) => (
                          <>
                            <div>{JSON.stringify(it).replace(/{|"|}/g, "").replace(":", ":\t")}</div>
                          </>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Wallet Transactions */}
                <div className="space-y-3">
                  <h6 className="text-md font-semibold">{t("O_WALLET_TRANSACTIONS")}</h6>
                  <div className="space-y-2">
                    {transactionData.wallet_transactions.map((wt) => (
                      <div key={wt._id} className="p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`p-2 rounded-lg ${wt.type === "credit" ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                              {wt.type === "credit" ? <BsArrowDownLeft className="h-4 w-4 text-emerald-400" /> : <BsArrowUpRight className="h-4 w-4 text-red-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <div>
                                  <p className="font-medium rounded-lg">
                                    {wt.type === "debit" ? `${wt.senderData.name} (${wt.senderData.userType})` : `${wt.receiverData.name} (${wt.receiverData.userType})`}
                                  </p>{" "}
                                  {wt.type === "debit" && wt.senderData.username && (
                                    <p className="font-thin text-sm">@{wt.senderData.username}</p>
                                  )}
                                  {wt.type === "credit" && wt.receiverData.username && (
                                    <p className="font-thin text-sm">@{wt.receiverData.username}</p>
                                  )}
                                </div>
                                <Badge
                                  variant="outline"
                                  className={`rounded-lg py-1 px-2 text-sm ${
                                    wt.type === "credit" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-red-500/10 text-red-400 border-red-500/20"
                                  }`}
                                >
                                  {wt.type.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="text-sm text-muted-foreground mb-1">{wt.message}</div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <span>
                                  {wt.type === "debit" ? wt.senderData.walletData?.wallet_description ?? wt.senderData.name : wt.receiverData.walletData?.wallet_description ?? wt.receiverData.name}
                                  {wt.type === "debit" && wt.senderData.walletData && (
                                    <span className="ps-1">
                                      (<span className="font-mono">{wt.senderData.walletData.wallet_number}</span>)
                                    </span>
                                  )}
                                  {wt.type === "credit" && wt.receiverData.walletData && (
                                    <span className="ps-1">
                                      (<span className="font-mono">{wt.receiverData.walletData.wallet_number}</span>)
                                    </span>
                                  )}
                                </span>
                                <span>•</span>
                                <span>{dayjs(wt.createdAt).format("DD/MM/YYYY, HH:mm:ss")}</span>
                              </div>
                              {transactionData.earnings
                                .filter((it) => it.wallet_transaction_id === wt._id)
                                .map((ea) => (
                                  <div className={`mt-2 rounded-md p-4 bg-LightBlue`}>
                                    <div className="flex justify-between">
                                      <div>
                                        <div className="flex gap-2 text-center items-center">
                                          <div>
                                            <div className={`h-5 w-7 me-2 mb-1 text-white text-lg font-bold`}>{ea.amount < 0 ? t("O_LIABILITY") : t("O_EARNING")}</div>
                                            <div className="flex gap-2 text-center items-center mt-2">
                                              <Badge variant="outline" className={`bg-white/30 backdrop-blur-sm text-white border-indigo-500/20 rounded-lg py-1 px-2 text-sm`}>
                                                {t(ea.earning_type)}
                                              </Badge>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="text-sm text-muted-foreground mt-2">
                                          {ea.description && <div className="text-emerald-600">{ea.description}</div>}
                                          {ea.failed_reason && (
                                            <div className="text-rose-600">
                                              {t("O_FAILED_REASON")}: {ea.failed_reason}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className={`text-lg font-bold text-white`}>
                                        {ea.amount < 0 ? "-" : "+"} {formatCurrency(ea.amount, ea.currency_code)}
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 rounded-lg bg-muted/30 w-100">
                                      {ea.exchange_rate && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_EXCHANGE_RATE")}</div>
                                          <div className="font-mono text-sm">{ea.exchange_rate}</div>
                                        </div>
                                      )}
                                      {ea.currency_from && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_CURRENCY_FROM")}</div>
                                          <div className="font-mono text-sm">{ea.currency_from}</div>
                                        </div>
                                      )}
                                      {ea.currency_to && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_CURRENCY_TO")}</div>
                                          <div className="font-mono text-sm">{ea.currency_to}</div>
                                        </div>
                                      )}
                                      {ea.original_amount && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_ORIGINAL_AMOUNT")}</div>
                                          <div className="font-mono text-sm">{ea.original_amount}</div>
                                        </div>
                                      )}
                                      {ea.comission_rate && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_COMMISSION_RATE")}</div>
                                          <div className="font-mono text-sm">{ea.comission_rate}</div>
                                        </div>
                                      )}
                                      {ea.commission_base_amount && (
                                        <div className="space-y-1">
                                          <div className="flex items-center gap-2 text-sm text-muted-foreground">{t("O_COMMISSION_BASE_AMOUNT")}</div>
                                          <div className="font-mono text-sm">{ea.commission_base_amount}</div>
                                        </div>
                                      )}
                                      {ea.metadata && ea.metadata.length > 0 && (
                                        <div className="space-y-1 w-100">
                                          <div className="text-sm text-muted-foreground">{t("O_METADATA")}</div>
                                          <div className="font-semibold">
                                            {ea.metadata.map((it) => (
                                              <>
                                                <div>{JSON.stringify(it).replace(/{|"|}/g, "").replace(":", ":\t")}</div>
                                              </>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}

                              {wt.metadata && wt.metadata.length > 0 && (
                                <div className="space-y-1 w-100">
                                  <div className="text-sm text-muted-foreground">{t("O_METADATA")}</div>
                                  <div className="font-semibold">
                                    {wt.metadata.map((it) => (
                                      <>
                                        <div>{JSON.stringify(it).replace(/{|"|}/g, "").replace(":", ":\t")}</div>
                                      </>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-semibold ${wt.type === "credit" ? "text-emerald-400" : "text-red-400"}`}>
                              {wt.type === "credit" ? "+ " : "- "}
                              {formatCurrency(wt.transaction_amount, wt.currency_code)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Invoice */}
                {transactionData.invoice != null && (
                  <>
                    <div className="space-y-3">
                      <h6 className="text-md font-semibold">{t("O_INVOICE")}</h6>
                      <div className="space-y-2 flex items-start justify-start">
                        <div
                          key={"invoice1"}
                          className="flex p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors w-1/2 items-center gap-4 cursor-pointer"
                          onClick={() => {
                            window.open(transactionData.invoice, "_blank");
                          }}
                        >
                          <BsFileTextFill className="h-7 w-7 text-emerald-400" />
                          <span className="text-sm">{transactionData.invoice.split("/").pop()}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {/* Attachments */}
                {transactionData.attachments.length > 0 && (
                  <>
                    <div className="space-y-3">
                      <h6 className="text-md font-semibold">{t("O_ATTACHMENTS")}</h6>
                      <div className="space-y-2 flex items-start justify-start">
                        {transactionData.attachments.map((at, idx) => (
                          <div
                            key={"attachment" + idx}
                            className="flex p-4 rounded-lg border border-border hover:bg-indigo-700/10 transition-colors w-1/2 items-center gap-4 cursor-pointer"
                            onClick={() => {
                              window.open(at, "_blank");
                            }}
                          >
                            <BsFileTextFill className="h-7 w-7 text-indigo-500" />
                            <span className="text-sm">{at.split("/").pop()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
                <button
                  className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150"
                  type="button"
                  onClick={() => setViewDetailsShowModal(false)}
                >
                  {t("O_BACK")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransactionDetailsView;
